/**
 * Auth API utilities
 * Shared authentication helpers for API clients
 */

import { throwApiError } from './client-error';
import { getAttribution } from '../lib/attribution';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787';
const AUTH_TOKENS_KEY = 'sda_auth_tokens';
const AUTH_USER_KEY = 'sda_auth_user';

/**
 * Force-logout on a rejected token.
 * When an authenticated request returns 401, the server has rejected the token
 * (e.g. the admin was removed / disabled). Clear stored auth and send the user
 * to the login page so they can't keep clicking a dead session.
 */
export function handleUnauthorized(response: Response): void {
  if (response.status !== 401) return;
  localStorage.removeItem(AUTH_TOKENS_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
  window.location.assign('/login');
}

export interface DormantMatch {
  studio_id: string;
  studio_name: string;
  address_line1: string;
  city: string;
  state: string;
  zip_code: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_phone: string;
}

interface AuthTokens {
  access_token: string;
  expires_at: number;
  refresh_token?: string;
}

/**
 * Get the auth token from localStorage
 * Must match the storage key used by AuthContext
 */
export function getAuthToken(): string | null {
  try {
    const tokensJson = localStorage.getItem(AUTH_TOKENS_KEY);
    if (!tokensJson) return null;
    const tokens = JSON.parse(tokensJson) as AuthTokens;
    // Check if token is expired
    if (tokens.expires_at * 1000 < Date.now()) {
      return null;
    }
    return tokens.access_token;
  } catch {
    return null;
  }
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return getAuthToken() !== null;
}

/**
 * Make an authenticated fetch request
 * Throws if not authenticated
 */
export async function authenticatedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const headers = new Headers(options.headers);
  headers.set('Authorization', `Bearer ${token}`);

  // Only set Content-Type for non-FormData requests
  // FormData sets its own Content-Type with boundary
  if (!(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });
  handleUnauthorized(response);
  return response;
}

/**
 * Make an authenticated fetch request that handles errors
 * Returns the parsed JSON response
 */
export async function authenticatedRequest<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await authenticatedFetch(url, options);
  const result = await response.json();

  if (!response.ok) {
    throwApiError(response, result, 'Request failed');
  }

  return result.data;
}

/**
 * Check an email for availability and dormant studio match.
 * Single source of truth for the /api/auth/check-email call shared by
 * RegisterPage, LoginPage (failed-login recognition), and ForgotPasswordPage.
 * Never throws — on network error returns { available: true } so callers
 * don't block the user on a transient failure.
 */
export async function checkEmail(
  email: string
): Promise<{ available: boolean; dormant_match?: DormantMatch }> {
  try {
    const response = await fetch(`${API_BASE}/api/auth/check-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const result = await response.json();
    const data = result?.data || {};
    return {
      available: data.available !== false,
      dormant_match: data.dormant_match as DormantMatch | undefined,
    };
  } catch {
    return { available: true };
  }
}

/**
 * Check phone number for dormant studio match
 */
export async function checkPhone(phone: string): Promise<{ dormant_match?: DormantMatch }> {
  const response = await fetch(`${API_BASE}/api/auth/check-phone`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone }),
  });
  const result = await response.json();
  return result.data || {};
}

/**
 * Activate a dormant studio account
 */
export async function activateDormant(data: {
  studio_id: string;
  email: string;
  password: string;
}): Promise<{
  session: { access_token: string; refresh_token: string; expires_at: number };
  user_id: string;
  studio_id: string;
}> {
  const response = await fetch(`${API_BASE}/api/auth/activate-dormant`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // SDA-267: attach stored campaign attribution out-of-band; the server
    // validates and stamps it (absent/invalid never blocks activation).
    body: JSON.stringify({ ...data, attribution: getAttribution() ?? undefined }),
  });
  const result = await response.json();
  if (!result.success) throwApiError(response, result, 'Activation failed');
  return result.data;
}
